import { SpanStatusCode, context, trace } from '@opentelemetry/api';
import PropTypes from 'prop-types';
import React from 'react';

import logger from 'src/util/logger';

/**
 * Reusable logic-only error boundary wrapper.
 * Centralises error-catching state and lifecycle handling so boundary-specific
 * wrappers can stay as clean functional components focused on rendering their
 * own fallback UI.
 * Must remain class-based because React currently provides no functional
 * equivalent for `getDerivedStateFromError` or `componentDidCatch`.
 * Ref  <https://react.dev/reference/react/Component#static-getderivedstatefromerror>
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The component subtree monitored for
 * render errors.
 * @param {React.ComponentType} props.fallbackComponent - Fallback component
 * rendered after an error; receives `error`, `errorInfo`, `resetFunc`,
 * `retryDisabled`, and `onCancelFunc`.
 * @param {Function|null} props.onCancelFunc - Callback function to be called when the user
 * cancels the error state. Return true after clearing the source error so the boundary can reset.
 * @param {Function|null} props.onRetryFunc - Callback function to be called when the user
 * requests a retry.
 * @param {boolean} props.retryDisabled - Disables the fallback retry action.
 */
class GenericErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('GenericErrorBoundary caught', error, errorInfo);
    const span = trace.getSpan(context.active());
    if (span) {
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    }

    this.setState({ errorInfo });
  }

  resetFunc = () => {
    const { onRetryFunc } = this.props;

    try {
      onRetryFunc?.();
    } finally {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
    }
  };

  /**
   * Resets the boundary to its non-error state without invoking `onRetryFunc`.
   * Use this from cancel/dismiss paths where the intent is to clear the error
   * UI without triggering a data re-fetch.
   */
  clearBoundaryFunc = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const {
      children, fallbackComponent: FallbackComponent, onCancelFunc, retryDisabled,
    } = this.props;
    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      return (
        <FallbackComponent
          error={error}
          errorInfo={errorInfo}
          onCancelFunc={onCancelFunc}
          resetFunc={this.resetFunc}
          clearBoundaryFunc={this.clearBoundaryFunc}
          retryDisabled={retryDisabled}
        />
      );
    }

    return children;
  }
}

GenericErrorBoundary.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  fallbackComponent: PropTypes.elementType.isRequired,
  onCancelFunc: PropTypes.func,
  onRetryFunc: PropTypes.func,
  retryDisabled: PropTypes.bool,
};

GenericErrorBoundary.defaultProps = {
  children: null,
  onCancelFunc: null,
  onRetryFunc: null,
  retryDisabled: false,
};

export default GenericErrorBoundary;
